import { Icon } from "../../components/Icon";
import { Skeleton } from "../../components/ui";
import { formatNumber, formatTime, formatToman } from "../../i18n/format";
import { useT } from "../../i18n/strings";
import type { PriceBoard } from "../../api/types";
import type { Quote } from "./quote";
import s from "../pieces/detail.module.css";

interface PriceBreakdownProps {
  quote: Quote;
  makingPercent: number | null;
  board: PriceBoard | undefined;
  loading?: boolean;
  /** A sold piece shows the price it was fixed at, not today's. */
  frozen?: { rial: number; at: string | null } | null;
  hint?: string;
}

/**
 * The read-only price: metal, making charge and stones adding up to a total, at
 * the rates it was worked out from. Nobody types into this.
 */
export function PriceBreakdown({
  quote,
  makingPercent,
  board,
  loading,
  frozen,
  hint,
}: PriceBreakdownProps) {
  const t = useT();

  if (frozen) {
    return (
      <div className={s.valuationStack}>
        <div className={s.valuation}>
          <div className={`${s.valuationRow} ${s.valuationTotal}`}>
            <span className={s.valuationLabel}>{t.detail.total}</span>
            <Money rial={frozen.rial} />
          </div>
        </div>
        {frozen.at && <Note text={t.detail.soldAt(formatTime(frozen.at))} />}
      </div>
    );
  }

  if (loading) return <Skeleton height={96} radius="var(--radius-card)" />;

  if (quote.total === null) {
    return <Note text={quote.missing.map((reason) => t.detail.missing[reason]).join(" ")} />;
  }

  return (
    <div className={s.valuationStack}>
      <div className={s.valuation}>
        {quote.metal !== null && quote.metal > 0 && (
          <div className={s.valuationRow}>
            <span className={s.valuationLabel}>{t.detail.metalValue}</span>
            <Money rial={quote.metal} />
          </div>
        )}
        {quote.making !== null && quote.making > 0 && makingPercent !== null && (
          <div className={s.valuationRow}>
            <span className={s.valuationLabel}>
              {t.detail.withMaking(formatNumber(makingPercent))}
            </span>
            <Money rial={quote.making} />
          </div>
        )}
        {quote.stones !== null && (
          <div className={s.valuationRow}>
            <span className={s.valuationLabel}>{t.detail.stonesValue}</span>
            <Money rial={quote.stones} />
          </div>
        )}
        <div className={`${s.valuationRow} ${s.valuationTotal}`}>
          <span className={s.valuationLabel}>{t.detail.total}</span>
          <output aria-live="polite">
            <Money rial={quote.total} />
          </output>
        </div>
      </div>
      <Note
        text={[
          hint,
          board &&
            `${t.prices.asOf(formatTime(board.source_updated_at ?? board.fetched_at))} · ${t.prices.source}.`,
          board?.stale && t.prices.stale,
        ]
          .filter(Boolean)
          .join(" ")}
      />
    </div>
  );
}

function Money({ rial }: { rial: number }) {
  const t = useT();
  return (
    <span style={{ whiteSpace: "nowrap" }}>
      <span className={s.valuationAmount}>{formatToman(rial)}</span>{" "}
      <span className={s.rowValueMuted}>{t.prices.toman}</span>
    </span>
  );
}

function Note({ text }: { text: string }) {
  if (!text) return null;
  return (
    <p className={s.evidenceNote}>
      <Icon name="info" size={16} />
      <span>{text}</span>
    </p>
  );
}
